/**
 * Minimal API client.
 *
 * Authentication is a session cookie set by the API, so every request sends
 * credentials and none of them carries a tenant identifier: the tenant is
 * derived from the session server-side and a client-supplied value is ignored
 * (CLAUDE.md, docs/plans/05-api-plan.md).
 */

export interface ApiErrorDetail {
  node_id?: string;
  path?: string;
  code: string;
  detail?: string;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  request_id: string;
  details?: ApiErrorDetail[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { Accept: "application/json", ...init?.headers },
  });
  const text = await response.text();
  const payload: unknown = text === "" ? null : JSON.parse(text);

  if (!response.ok) {
    const envelope = payload as { error?: ApiErrorBody } | null;
    throw new ApiError(
      response.status,
      envelope?.error ?? {
        code: "unknown",
        message: `request failed with status ${response.status}`,
        request_id: response.headers.get("X-Request-Id") ?? "",
      },
    );
  }
  return payload as T;
}

export interface HealthResponse {
  status: string;
  version: string;
}

export interface MediaReadiness {
  status: string;
  media_plane: string;
  adapter: string;
  instance_id: string;
}

export interface Identity {
  user_id: string;
  tenant_id: string;
  role: string;
  platform_admin: boolean;
  /** The caller's own recovery email (PATCH /v1/auth/me). null until set —
   * self-service forgot-password only works once this is non-null. */
  email: string | null;
}

export interface LoginResponse extends Identity {
  csrf_token: string;
}

export interface Invite {
  invite_id: string;
  username: string;
  role: string;
  created_at: string;
  expires_at: string;
}

export interface ApiKey {
  api_key_id: string;
  label: string;
  can_place_calls: boolean;
  can_read_reports: boolean;
  created_at: string;
  expires_at?: string;
  revoked_at?: string;
  last_used_at?: string;
}

export interface ApiKeyCreated {
  api_key_id: string;
  label: string;
  can_place_calls: boolean;
  can_read_reports: boolean;
  /** Plaintext API key, returned exactly once — the same one-time-disclosure
   * shape InviteCreated's own token already uses. If it's lost, revoke this
   * key and issue a new one; nothing in this API can show it again. */
  token: string;
  created_at: string;
}

export interface InviteCreated {
  invite_id: string;
  username: string;
  role: string;
  /** Plaintext, returned exactly once — see the API's own doc comment: there
   * is no email integration yet, so the inviting admin relays this out of
   * band. */
  token: string;
  expires_at: string;
}

export interface PasswordResetIssued {
  /** Plaintext, returned exactly once. */
  token: string;
  expires_at: string;
}

export interface FlowVersion {
  flow_version_id: string;
  name: string;
  published_at: string;
}

export interface PublishedFlow extends FlowVersion {
  program: unknown;
}

/**
 * One saved template. `flow_id` identifies the template across its
 * versions; `latest_version_id` is the flow_version_id to dial or to give
 * an API caller as `template-id`.
 */
export interface FlowTemplate {
  flow_id: string;
  name: string;
  version_count: number;
  latest_version_id: string;
  latest_version_number: number;
  latest_published_at: string;
  created_at: string;
}

export interface TemplateVersion {
  flow_version_id: string;
  version_number: number;
  name: string;
  published_at: string;
}

/** What POST /v1/flows answers: the version, and the template it joined. */
export interface PublishedFlowVersion extends FlowVersion {
  flow_id: string;
  version_number: number;
}

export interface Asset {
  asset_id: string;
  status: string;
  created_at: string;
}

export interface Voice {
  voice: string;
  language: string;
  created_at: string;
}

export interface AdhocCall {
  adhoc_call_id: string;
  status: "pending" | "dispatched" | "failed";
  call_id?: string;
  error?: string;
  created_at: string;
  dispatched_at?: string;
}

export interface CallSummary {
  id: string;
  direction: string;
  media_plane: string;
  status: string;
  terminal_reason?: string;
  /** "NOT_RUN" unless an amd_gate node actually ran on this call. */
  amd_result: "HUMAN" | "MACHINE" | "NOTSURE" | "NOT_RUN";
  created_at: string;
  ended_at?: string;
  /**
   * The FIRST flow node that failed and took its error path, if any. Absent
   * when nothing failed, so their presence alone distinguishes a call that
   * genuinely did what it was asked from one that completed having played
   * nothing — both of which report terminal_reason "completed".
   */
  flow_error_node_id?: string;
  flow_error_node_type?: string;
  flow_error?: string;
  /** Total error-path transitions. 0 when nothing failed; >1 means flow_error is the first of several. */
  flow_error_count: number;
}

export interface AdhocCallSummary {
  adhoc_call_id: string;
  /** This request's own status — "pending" until cmd/worker claims it. */
  status: "pending" | "dispatched" | "failed";
  error?: string;
  destination: string;
  trunk_ref: string;
  caller_id: string;
  created_at: string;
  dispatched_at?: string;
  /** A POST /v1/send-msg caller's own uid and type label; absent otherwise. */
  uid?: string;
  type?: string;
  /** Which template ran this call, and which of its versions. */
  flow_id?: string;
  template_name?: string;
  version_number?: number;
  call_id?: string;
  /** All of these are absent/undefined until the request is actually dispatched. */
  call_status?: string;
  terminal_reason?: string;
  amd_result?: "HUMAN" | "MACHINE" | "NOTSURE" | "NOT_RUN";
  /** Which detector rule produced amd_result — absent when amd_result itself is absent or NOT_RUN. */
  amd_cause?: string;
  /** The real SIP/media hangup cause, e.g. NORMAL_CLEARING, NO_ANSWER, USER_BUSY. */
  hangup_cause?: string;
  answered_at?: string;
  ended_at?: string;
  /** See CallSummary — the first flow node that failed and took its error path. */
  flow_error_node_id?: string;
  flow_error_node_type?: string;
  flow_error?: string;
  flow_error_count: number;
  /**
   * What the caller pressed, in order. Always present — `[]` means the
   * call reached no gather node, or nobody pressed anything, which is a
   * different fact from "we have no information".
   */
  key_presses: KeyPress[];
}

/** One gather node's outcome on one call. */
export interface KeyPress {
  node_id: string;
  /** The prompt as authored, with {{variables}} left unsubstituted. */
  prompt: string;
  /** The digit, or "" for a timeout or invalid key — tell them apart by outcome. */
  key: string;
  outcome: "key" | "timeout" | "invalid";
  /** Prompt plays before this outcome; 1 means first time. */
  attempts: number;
}

function authedJSON<T>(path: string, csrfToken: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify(body),
  });
}

export const api = {
  health: () => request<HealthResponse>("/healthz"),
  ready: () => request<{ status: string }>("/readyz"),
  mediaReady: () => request<MediaReadiness>("/readyz/media"),
  me: () => request<Identity>("/v1/auth/me"),
  login: (username: string, password: string) =>
    request<LoginResponse>("/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    }),
  logout: (csrfToken: string) =>
    request<{ status: string }>("/v1/auth/logout", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
    }),
  listApiKeys: () => request<{ api_keys: ApiKey[] }>("/v1/api-keys"),
  createApiKey: (csrfToken: string, label: string, canPlaceCalls: boolean, canReadReports: boolean) =>
    authedJSON<ApiKeyCreated>("/v1/api-keys", csrfToken, {
      label,
      can_place_calls: canPlaceCalls,
      can_read_reports: canReadReports,
    }),
  revokeApiKey: (csrfToken: string, apiKeyId: string) =>
    authedJSON<{ status: string }>(`/v1/api-keys/${encodeURIComponent(apiKeyId)}/revoke`, csrfToken, {}),
  listInvites: () => request<{ invites: Invite[] }>("/v1/invites"),
  createInvite: (csrfToken: string, username: string, role: string) =>
    authedJSON<InviteCreated>("/v1/invites", csrfToken, { username, role }),
  revokeInvite: (csrfToken: string, inviteId: string) =>
    authedJSON<{ status: string }>(`/v1/invites/${encodeURIComponent(inviteId)}/revoke`, csrfToken, {}),
  acceptInvite: (token: string, password: string) =>
    request<LoginResponse>("/v1/invites/accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    }),
  requestPasswordReset: (csrfToken: string, username: string) =>
    authedJSON<PasswordResetIssued>("/v1/password-resets", csrfToken, { username }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ status: string }>("/v1/auth/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
  /** Always the same generic response regardless of what actually happened
   * server-side (httpx/forgotpassword.go) -- never treat this as a signal
   * about whether the username exists. */
  forgotPassword: (username: string) =>
    request<{ status: string }>("/v1/auth/forgot-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    }),
  setMyEmail: (csrfToken: string, email: string) =>
    request<{ email: string }>("/v1/auth/me", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ email }),
    }),
  /**
   * `flowId` publishes the next version of an existing template — what Save
   * does after an edit, since a published version is never mutated. `kind`
   * is read only when starting a new template: "quick_message" keeps the
   * place-a-call form's throwaway publishes out of the Templates list.
   */
  publishFlow: (
    csrfToken: string,
    name: string,
    program: unknown,
    options?: { flowId?: string; kind?: "template" | "quick_message" },
  ) =>
    authedJSON<PublishedFlowVersion>("/v1/flows", csrfToken, {
      name,
      program,
      ...(options?.flowId ? { flow_id: options.flowId } : {}),
      ...(options?.kind ? { kind: options.kind } : {}),
    }),
  listFlowVersions: () => request<{ flow_versions: FlowVersion[] }>("/v1/flows"),
  /** Saved templates only — no quick messages, no archived ones. */
  listTemplates: () => request<{ templates: FlowTemplate[] }>("/v1/templates"),
  listTemplateVersions: (flowId: string) =>
    request<{ versions: TemplateVersion[] }>(`/v1/templates/${encodeURIComponent(flowId)}/versions`),
  /** Archives: the template leaves the list, its versions stay so call history survives. */
  archiveTemplate: (csrfToken: string, flowId: string) =>
    authedJSON<void>(`/v1/templates/${encodeURIComponent(flowId)}/archive`, csrfToken, {}),
  getFlowVersion: (flowVersionId: string) =>
    request<PublishedFlow>(`/v1/flows/${encodeURIComponent(flowVersionId)}`),
  listAssets: () => request<{ assets: Asset[] }>("/v1/assets"),
  registerAsset: (csrfToken: string, assetId: string) =>
    authedJSON<Asset>("/v1/assets", csrfToken, { asset_id: assetId }),
  listVoices: () => request<{ voices: Voice[] }>("/v1/voices"),
  registerVoice: (csrfToken: string, voice: string, language: string) =>
    authedJSON<Voice>("/v1/voices", csrfToken, { voice, language }),
  /**
   * `variables` supplies the per-call values a template declares. The
   * backend has always carried them (adhoc_calls.variables, read by
   * cmd/worker into the flow's VariableStore); until Slice DI this client
   * never sent any, so a template with a required variable refused to
   * start and the call hung up with no audio.
   */
  placeAdhocCall: (
    csrfToken: string,
    flowVersionId: string,
    destination: string,
    trunkRef: string,
    callerId?: string,
    variables?: Record<string, string | number | boolean>,
  ) =>
    authedJSON<AdhocCall>("/v1/adhoc-calls", csrfToken, {
      flow_version_id: flowVersionId,
      destination,
      trunk_ref: trunkRef,
      caller_id: callerId ?? "",
      ...(variables && Object.keys(variables).length > 0 ? { variables } : {}),
    }),
  getAdhocCall: (adhocCallId: string) =>
    request<AdhocCall>(`/v1/adhoc-calls/${encodeURIComponent(adhocCallId)}`),
  listCalls: () => request<{ calls: CallSummary[] }>("/v1/calls"),
  /** `flowId` narrows the list to one template, across all its versions. */
  listAdhocCalls: (flowId?: string) =>
    request<{ adhoc_calls: AdhocCallSummary[] }>(
      "/v1/adhoc-calls" + (flowId ? `?flow_id=${encodeURIComponent(flowId)}` : ""),
    ),
};
